import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

export const API_RUEDA = process.env.REACT_APP_BACKEND_URL + '/api/rueda-import';
const ESTADOS = { en_curso: 'En curso', completado: 'Completado', error: 'Error' };
const CAT_LABEL = { turismo: 'Turismo', convenios: 'Convenios', camping: 'Camping y recreos', eventos: 'Eventos y novedades' };
const CAMPO_LABEL = { descripcion_larga: 'descripción', servicios: 'servicios', condiciones: 'condiciones', horarios: 'horarios', precios: 'precios', otros: 'otros datos' };

const Num = ({ n, label }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-2"><p className="text-lg font-bold">{n}</p><p className="text-[11px] text-gray-500">{label}</p></div>
);

const LogDetalle = ({ id }) => {
  const [log, setLog] = useState(null);
  useEffect(() => { axios.get(`${API_RUEDA}/sync/logs/${id}`).then((r) => setLog(r.data)); }, [id]);
  if (!log) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <div className="space-y-2 text-xs" data-testid={`rueda-log-detalle-${id}`}>
      {log.detalle.map((d, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2 border-b py-1">
          <span className="font-medium">{d.nombre}</span>
          <span className={`rounded px-1.5 ${d.accion === 'nuevo' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{d.accion}</span>
          {d.sin_categorizar && <span className="rounded bg-amber-100 px-1.5 text-amber-700">sin categorizar</span>}
          <span className="text-gray-500">{d.pdfs} PDF</span>
          {d.campos_ia.length > 0 && <span className="flex items-center gap-1 text-violet-700"><Sparkles className="h-3 w-3" />IA: {d.campos_ia.map((c) => CAMPO_LABEL[c] || c).join(', ')}</span>}
        </div>
      ))}
      {!log.detalle.length && <p className="text-gray-400">Sin novedades: nada nuevo ni modificado.</p>}
      {log.errores.length > 0 && <div className="text-red-600">{log.errores.map((e, i) => <p key={i}>{e}</p>)}</div>}
    </div>
  );
};

const AvisosConfig = () => {
  const [emails, setEmails] = useState('');
  const [defecto, setDefecto] = useState([]);
  useEffect(() => {
    axios.get(`${API_RUEDA}/avisos`).then((r) => { setEmails(r.data.emails.join(', ')); setDefecto(r.data.por_defecto); });
  }, []);
  const guardar = async () => {
    try {
      const r = await axios.put(`${API_RUEDA}/avisos`, { emails: emails.split(/[,;\s]+/).filter(Boolean) });
      setEmails(r.data.emails.join(', ')); toast.success('Destinatarios guardados');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo guardar'); }
  };
  return (
    <div className="rounded-xl border bg-white p-4 space-y-2" data-testid="rueda-avisos-config">
      <p className="text-sm font-medium">Aviso por email de novedades</p>
      <p className="text-xs text-gray-500">Cuando la sincronización mensual encuentra prestadores nuevos o con cambios, se manda un email con la lista.
        {!emails && defecto.length > 0 && ` Si lo dejás vacío se envía a los administradores: ${defecto.join(', ')}.`}</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="email1@..., email2@..." data-testid="rueda-avisos-emails" />
        <Button size="sm" variant="outline" onClick={guardar} data-testid="rueda-avisos-guardar">Guardar</Button>
      </div>
    </div>
  );
};

export const RuedaSyncPanel = ({ onFin }) => {
  const [logs, setLogs] = useState([]);
  const [abierto, setAbierto] = useState(null);
  const cargar = useCallback(() => axios.get(`${API_RUEDA}/sync/logs`).then((r) => setLogs(r.data.items)), []);
  const enCurso = logs.some((l) => l.estado === 'en_curso');

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!enCurso) return undefined;
    const t = setInterval(() => cargar().then(() => onFin?.()), 5000);
    return () => clearInterval(t);
  }, [enCurso, cargar, onFin]);

  const sincronizar = async () => {
    try { await axios.post(`${API_RUEDA}/sync`); toast.success('Sincronización iniciada'); cargar(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo iniciar'); }
  };

  return (
    <div className="space-y-4" data-testid="rueda-sync-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">Corre sola el día 1 de cada mes a las 3 AM. También podés correrla ahora.</p>
        <Button onClick={sincronizar} disabled={enCurso} data-testid="rueda-sync-btn">
          {enCurso ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          {enCurso ? 'Sincronizando…' : 'Sincronizar ahora'}
        </Button>
      </div>
      <AvisosConfig />
      {logs.map((l) => (
        <div key={l.id} className="rounded-xl border bg-white p-4 space-y-3" data-testid={`rueda-log-${l.id}`}>
          <button className="flex w-full items-center gap-2 text-left text-sm" onClick={() => setAbierto(abierto === l.id ? null : l.id)}>
            {abierto === l.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <b>{new Date(l.inicio).toLocaleString('es-AR')}</b>
            <span className="text-gray-500">· {l.origen === 'automatico' ? 'automática' : `manual (${l.usuario})`}</span>
            <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${l.estado === 'error' ? 'bg-red-100 text-red-700' : l.estado === 'en_curso' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{ESTADOS[l.estado]}</span>
          </button>
          {l.error && <p className="text-xs text-red-600">{l.error}</p>}
          {l.por_categoria && Object.keys(l.por_categoria).length > 0 && (
            <p className="text-xs text-gray-500" data-testid={`rueda-log-categorias-${l.id}`}>
              {Object.entries(l.por_categoria).map(([k, n]) => `${CAT_LABEL[k] || k}: ${n}`).join(' · ')}
            </p>
          )}
          {l.estado === 'en_curso' && (
            <button className="text-xs text-red-600 underline" data-testid={`rueda-reset-${l.id}`}
              onClick={async () => {
                if (!window.confirm('¿Destrabar esta sincronización? Se marcará como interrumpida y podrás correr una nueva.')) return;
                try { await axios.post(`${API_RUEDA}/sync/logs/${l.id}/reset`); toast.success('Sincronización destrabada'); cargar(); }
                catch (e) { toast.error(e.response?.data?.detail || 'No se pudo destrabar'); }
              }}>
              ¿Quedó trabada? Destrabar
            </button>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <Num n={l.encontrados} label="Encontrados" /><Num n={l.nuevos} label="Nuevos" /><Num n={l.actualizados} label="Actualizados" />
            <Num n={l.sin_cambios} label="Sin cambios" /><Num n={l.pdfs_analizados} label="PDFs analizados" /><Num n={l.errores?.length || 0} label="Errores" />
          </div>
          {abierto === l.id && <LogDetalle id={l.id} />}
        </div>
      ))}
      {!logs.length && <p className="text-sm text-gray-400" data-testid="rueda-logs-vacio">Todavía no se corrió ninguna sincronización.</p>}
    </div>
  );
};
